<template>
  <div class="planos-manager">
    <div class="header-actions">
      <p class="hint">Os planos sao as opcoes que o inscrito tem pra pagar (a vista ou em parcelas).</p>
      <button v-if="!showAdd" type="button" class="btn-primary" @click="abrirAdd" :disabled="loading">+ Novo plano</button>
    </div>

    <div v-if="showAdd" class="add-form">
      <h4>Novo plano</h4>
      <div class="add-grid">
        <div class="form-row">
          <label class="label">Nome</label>
          <input class="input" v-model="novo.nome" placeholder="Ex: PIX a vista" />
        </div>
        <div class="form-row">
          <label class="label">Valor (R$)</label>
          <input class="input" type="number" min="0" step="0.01" v-model.number="novo.valor" />
        </div>
        <div class="form-row">
          <label class="label">Tipo</label>
          <select class="select" v-model="novo.tipo">
            <option value="UNICO">A vista (1 pagamento)</option>
            <option value="MENSAL">Mensal recorrente</option>
          </select>
        </div>
        <div class="form-row">
          <label class="label">Parcelas</label>
          <input class="input" type="number" min="2" max="36" v-model.number="novo.parcelas" placeholder="vazio = cobranca unica" />
          <span class="hint-inline">2 ou mais gera carne, seja o tipo qual for. Vazio cobra uma vez so.</span>
        </div>
        <div class="form-row">
          <label class="label">Gateway</label>
          <select class="select" v-model="novo.gateway">
            <option value="ASAAS">Asaas — taxa fixa R$ 1,99</option>
            <option value="MERCADO_PAGO">Mercado Pago — 0,99%</option>
          </select>
          <span class="hint-inline">Taxa fixa compensa em valor alto; percentual, em parcela pequena. Vira ~R$ 200.</span>
        </div>
        <div v-if="novo.tipo === 'MENSAL'" class="form-row">
          <label class="label">1o vencimento</label>
          <input class="input" type="date" v-model="novo.vencimentoPrimeiroPagamento" />
        </div>
      </div>
      <div v-if="addError" class="error-msg">{{ addError }}</div>
      <div class="add-actions">
        <button type="button" class="btn-secondary" @click="cancelarAdd">Cancelar</button>
        <button type="button" class="btn-primary" @click="adicionar" :disabled="adding">{{ adding ? 'Salvando...' : 'Adicionar' }}</button>
      </div>
    </div>

    <SkeletonLoader v-if="loading" variant="row" :count="3" />

    <ul v-else-if="planos.length" class="list">
      <li v-for="p in planos" :key="p.id" class="item" :class="{ inativo: !p.ativo }">
        <div class="info">
          <strong>{{ p.nome }}</strong>
          <span class="tipo">{{ rotuloParcelas(p) }}</span>
          <span class="valor">{{ formatMoney(p.valor) }}</span>
        </div>
        <div class="actions">
          <select
            class="select-gateway"
            :value="p.gateway || 'ASAAS'"
            :disabled="salvandoGateway === p.id"
            @change="trocarGateway(p, $event.target.value)"
          >
            <option value="ASAAS">Asaas</option>
            <option value="MERCADO_PAGO">Mercado Pago</option>
          </select>
          <button type="button" class="link-btn" @click="toggle(p)">{{ p.ativo ? 'Inativar' : 'Ativar' }}</button>
          <button type="button" class="link-btn link-danger" @click="remover(p)">Remover</button>
        </div>
      </li>
    </ul>

    <p v-else-if="!showAdd" class="empty">Nenhum plano cadastrado. Clique em "+ Novo plano" pra criar.</p>
  </div>
</template>

<script>
import api from '../../lib/api'
import SkeletonLoader from '../ui/SkeletonLoader.vue'

// `parcelas` nasce vazio de proposito: com o campo sempre visivel, um default
// de 10 transformaria todo plano a vista num carne de 10 sem ninguem pedir.
function emptyNovo() {
  return { nome: '', valor: 0, tipo: 'UNICO', parcelas: null, gateway: 'ASAAS', vencimentoPrimeiroPagamento: '' }
}

export default {
  name: 'PlanosManager',
  components: { SkeletonLoader },
  props: { eventoId: { type: String, required: true } },
  data() {
    return { planos: [], loading: false, adding: false, novo: emptyNovo(), addError: '', showAdd: false, salvandoGateway: null }
  },
  async mounted() { await this.load() },
  methods: {
    abrirAdd() {
      this.novo = emptyNovo()
      this.addError = ''
      this.showAdd = true
    },
    cancelarAdd() {
      this.showAdd = false
      this.addError = ''
    },
    formatMoney(v) {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)
    },
    // Quem define o carne e `parcelas >= 2`, nao o tipo: os planos parcelados do
    // ACAMP 2027 estao cadastrados como UNICO. Ler o tipo aqui mostraria "A
    // vista" num plano de 9 parcelas.
    rotuloParcelas(p) {
      return p.parcelas >= 2 ? `${p.parcelas}x` : 'A vista'
    },
    async trocarGateway(p, gateway) {
      if (gateway === (p.gateway || 'ASAAS')) return
      const anterior = p.gateway
      this.salvandoGateway = p.id
      try {
        await api.patch(`/planos/${p.id}`, { gateway })
        p.gateway = gateway
        this.$toast?.open?.({ message: `Plano agora cobra pelo ${gateway === 'ASAAS' ? 'Asaas' : 'Mercado Pago'}.`, type: 'success', position: 'top-right' })
      } catch (err) {
        p.gateway = anterior
        this.$toast?.open?.({ message: err.response?.data?.message || 'Erro ao trocar o gateway', type: 'error', position: 'top-right' })
      } finally {
        this.salvandoGateway = null
      }
    },
    async load() {
      this.loading = true
      try {
        const { data } = await api.get('/planos', { params: { eventoId: this.eventoId } })
        this.planos = data.planos
      } finally { this.loading = false }
    },
    async toggle(p) {
      try {
        await api.patch(`/planos/${p.id}`, { ativo: !p.ativo })
        p.ativo = !p.ativo
      } catch (err) {
        this.$toast?.open?.({ message: err.response?.data?.message || 'Erro', type: 'error', position: 'top-right' })
      }
    },
    async remover(p) {
      if (!confirm(`Remover plano "${p.nome}"?`)) return
      try {
        await api.delete(`/planos/${p.id}`)
        this.planos = this.planos.filter(x => x.id !== p.id)
      } catch (err) {
        this.$toast?.open?.({ message: err.response?.data?.message || 'Erro', type: 'error', position: 'top-right' })
      }
    },
    async adicionar() {
      this.addError = ''
      if (!this.novo.nome || this.novo.valor <= 0) { this.addError = 'Preencha nome e valor.'; return }
      if (this.novo.tipo === 'MENSAL' && (!this.novo.parcelas || !this.novo.vencimentoPrimeiroPagamento)) {
        this.addError = 'Defina parcelas e 1o vencimento.'; return
      }
      this.adding = true
      try {
        const payload = {
          eventoId: this.eventoId,
          nome: this.novo.nome,
          valor: this.novo.valor,
          tipo: this.novo.tipo,
          ordem: this.planos.length
        }
        if (this.novo.gateway) payload.gateway = this.novo.gateway
        // Enviado sempre que preenchido, nao so em MENSAL: era esse `if` que
        // deixava um plano parcelado cadastrado como UNICO nascer sem carne.
        if (this.novo.parcelas >= 2) payload.parcelas = this.novo.parcelas
        if (this.novo.tipo === 'MENSAL') {
          payload.vencimentoPrimeiroPagamento = this.novo.vencimentoPrimeiroPagamento
        }
        const { data } = await api.post('/planos', payload)
        this.planos.push(data.plano)
        this.novo = emptyNovo()
        this.showAdd = false
      } catch (err) {
        this.addError = err.response?.data?.message || 'Falha ao criar plano'
      } finally {
        this.adding = false
      }
    }
  }
}
</script>

<style scoped>
.planos-manager { display: flex; flex-direction: column; gap: var(--spacing-base); }
.header-actions { display: flex; align-items: center; justify-content: space-between; gap: var(--spacing-md); }
.hint { color: var(--color-text-muted); font-size: var(--text-sm); margin: 0; flex: 1; }
.add-actions { display: flex; gap: var(--spacing-sm); justify-content: flex-end; margin-top: var(--spacing-sm); }
.list { list-style: none; display: flex; flex-direction: column; gap: var(--spacing-sm); }
.item {
  display: flex; align-items: center; gap: var(--spacing-base);
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--color-bg-hover);
  border-radius: var(--radius-md);
}
.item.inativo { opacity: 0.5; }
.info { display: flex; align-items: center; gap: var(--spacing-sm); flex: 1; flex-wrap: wrap; }
.tipo { font-size: var(--text-xs); color: var(--color-text-muted); }
.valor { margin-left: auto; font-weight: 700; color: var(--color-accent); }
.actions { display: flex; gap: 4px; }
.link-btn { background: none; border: none; cursor: pointer; color: var(--color-accent); font-size: var(--text-xs); }
.select-gateway {
  font-size: var(--text-xs); padding: 2px 6px; border-radius: var(--radius-sm);
  border: 1px solid var(--color-bg-separator); background: var(--color-bg); color: var(--color-text-muted);
}
.select-gateway:disabled { opacity: 0.5; }
.hint-inline { font-size: var(--text-xs); color: var(--color-text-muted); }
.link-danger { color: var(--color-danger); }
.empty { color: var(--color-text-muted); font-size: var(--text-sm); text-align: center; padding: var(--spacing-base); }
.add-form { border: 1px dashed var(--color-bg-separator); border-radius: var(--radius-md); padding: var(--spacing-base); display: flex; flex-direction: column; gap: var(--spacing-sm); }
.add-grid { display: grid; grid-template-columns: 1fr 1fr; gap: var(--spacing-sm); }
.form-row { display: flex; flex-direction: column; gap: 4px; }
@media (max-width: 540px) {
  .add-grid { grid-template-columns: 1fr; }
}
</style>
